import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { Download, Eye, FileSpreadsheet, Lock, Loader2, Plus, RefreshCw, ShieldCheck, X } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../config/adminRoutes";
import FinanceAccessDenied from "../../components/finance/FinanceAccessDenied";
import { AdminKpiCard, AdminPageHero, adminHeroActionClassName } from "../../modules/admin/ui";

type FinancialClosingStatus = "DRAFT" | "PREVIEWED" | "VALIDATED" | "LOCKED" | "CANCELLED";
type FinancialClosingType = "MONTHLY" | "YEARLY";
type UserRef = { id: number; nom: string; prenom: string | null; email?: string | null } | null;

type FinancialClosingSnapshotDto = {
  period: { label: string; start: string; end: string; fiscalYear: number; month: number | null };
  totals: { totalHt: number; taxAmount: number; totalTtc: number; totalPaid: number; totalUnpaid: number };
  counts: { invoiceCount: number; paidInvoiceCount: number; unpaidInvoiceCount: number; paymentCount: number; receiptCount: number; anomalyCount: number };
  invoices: Array<{ id: number; number: string; client: string; status: string; totalTtc: number; paidAmount: number; remainingBalance: number }>;
  payments: Array<{ id: number; paidAt: string; amount: number; method: string; invoiceNumber: string | null; hasReceipt: boolean }>;
  unpaidInvoices: Array<{ id: number; number: string; client: string; remainingBalance: number }>;
  anomalies: Array<{ type: string; entity: string; entityId: number; message: string }>;
};

type FinancialClosingDto = {
  id: number;
  type: FinancialClosingType;
  status: FinancialClosingStatus;
  periodStart: string;
  periodEnd: string;
  fiscalYear: number;
  month: number | null;
  totalInvoiced: string | number;
  totalPaid: string | number;
  totalUnpaid: string | number;
  invoiceCount: number;
  paidInvoiceCount: number;
  unpaidInvoiceCount: number;
  paymentCount: number;
  receiptCount: number;
  anomalyCount: number;
  snapshotJson: FinancialClosingSnapshotDto | null;
  notes: string | null;
  createdAt: string;
  previewedAt: string | null;
  validatedAt: string | null;
  lockedAt: string | null;
  cancelledAt: string | null;
  createdBy: UserRef;
  previewedBy: UserRef;
  validatedBy: UserRef;
  lockedBy: UserRef;
  audits?: Array<{ id: number; action: string; createdAt: string; actor: UserRef }>;
};

type ClosingsResponse = {
  items: FinancialClosingDto[];
  financeHints?: {
    activeFiscalYear: number;
    lastValidatedMonthly: FinancialClosingDto | null;
    openMonthsInActiveFiscalYear: number;
  };
};

function money(value: string | number | null | undefined): string {
  const n = Number(value ?? 0);
  return `${(Number.isFinite(n) ? n : 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
}

function userName(user: UserRef): string {
  if (!user) return "—";
  return [user.prenom, user.nom].filter(Boolean).join(" ") || user.email || "Utilisateur";
}

function typeLabel(type: FinancialClosingType): string {
  return type === "MONTHLY" ? "Mensuelle" : "Annuelle";
}

function statusLabel(status: FinancialClosingStatus): string {
  return {
    DRAFT: "Brouillon",
    PREVIEWED: "Prévisualisée",
    VALIDATED: "Validée",
    LOCKED: "Verrouillée",
    CANCELLED: "Annulée",
  }[status];
}

function periodLabel(row: FinancialClosingDto): string {
  if (row.type === "YEARLY") return String(row.fiscalYear);
  return `${row.fiscalYear}-${String(row.month ?? dayjs(row.periodStart).month() + 1).padStart(2, "0")}`;
}

function apiError(error: unknown, fallback: string): string {
  return isAxiosError(error) ? (error.response?.data as { message?: string } | undefined)?.message ?? fallback : fallback;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function CloturesFinancieresPage(): React.JSX.Element {
  const { user, isLoading: authLoading } = useAuth();
  const allowed = useMemo(() => hasPermission(user, "clotures.view"), [user]);
  const isAdmin = String(user?.role ?? "").toUpperCase() === "ADMIN";

  const [rows, setRows] = useState<FinancialClosingDto[]>([]);
  const [hints, setHints] = useState<ClosingsResponse["financeHints"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<FinancialClosingDto | null>(null);
  const [preview, setPreview] = useState<FinancialClosingSnapshotDto | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [type, setType] = useState<FinancialClosingType>("MONTHLY");
  const [periodStart, setPeriodStart] = useState(() => dayjs().startOf("month").format("YYYY-MM-DD"));
  const [periodEnd, setPeriodEnd] = useState(() => dayjs().endOf("month").format("YYYY-MM-DD"));
  const [fiscalYear, setFiscalYear] = useState(() => String(dayjs().year()));
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const { data } = await api.get<ClosingsResponse>("/finance/closings");
      setRows(Array.isArray(data.items) ? data.items : []);
      setHints(data.financeHints ?? null);
    } catch {
      toast.error("Impossible de charger les clôtures.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [allowed]);

  useEffect(() => {
    void load();
  }, [load]);

  const lastMonthly = useMemo(() => {
    if (hints?.lastValidatedMonthly) return hints.lastValidatedMonthly;
    return rows.find((row) => row.type === "MONTHLY" && (row.status === "VALIDATED" || row.status === "LOCKED")) ?? null;
  }, [hints?.lastValidatedMonthly, rows]);

  const runPreview = async (closing?: FinancialClosingDto) => {
    setSelected(closing ?? null);
    setPreview(null);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const { data } = await api.post<{ data: FinancialClosingSnapshotDto }>("/finance/closings/preview", {
        closingId: closing?.id,
        type: closing ? undefined : type,
        periodStart: closing ? undefined : periodStart,
        periodEnd: closing ? undefined : periodEnd,
      });
      setPreview(data.data);
      if (closing) void load();
    } catch (error) {
      toast.error(apiError(error, "Échec de la prévisualisation."));
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const createDraft = async () => {
    try {
      await api.post("/finance/closings/draft", { type, periodStart, periodEnd, fiscalYear: Number(fiscalYear), notes });
      toast.success("Clôture brouillon créée.");
      setCreateOpen(false);
      void load();
    } catch (error) {
      toast.error(apiError(error, "Création impossible."));
    }
  };

  const runAction = async (closing: FinancialClosingDto, action: "validate" | "lock" | "cancel") => {
    try {
      await api.post(`/finance/closings/${closing.id}/${action}`, action === "cancel" ? { reason: "Annulation depuis l’interface finance." } : {});
      toast.success(action === "validate" ? "Clôture validée." : action === "lock" ? "Période verrouillée." : "Clôture annulée.");
      void load();
    } catch (error) {
      toast.error(apiError(error, "Action impossible."));
    }
  };

  const exportFile = async (closing: FinancialClosingDto, kind: "pdf" | "excel") => {
    try {
      const response = await api.get(`/finance/closings/${closing.id}/export/${kind}`, { responseType: "blob" });
      downloadBlob(response.data, kind === "pdf" ? `cloture-financiere-${closing.id}.pdf` : `cloture-financiere-${closing.id}.xlsx`);
    } catch {
      toast.error("Export impossible.");
    }
  };

  if (authLoading) return <div className="flex min-h-[40vh] items-center justify-center text-slate-500"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!allowed) return <FinanceAccessDenied />;

  return (
    <div className="flex w-full min-w-0 max-w-none flex-col gap-6">
      <AdminPageHero
        eyebrow="Finance"
        title="Clôtures financières"
        subtitle="Prévisualisation, validation, verrouillage, snapshot, audit et exports comptables."
        actions={<div className="flex flex-wrap gap-2">
          <button type="button" className={adminHeroActionClassName(false)} onClick={() => void load()} disabled={loading}><RefreshCw className="h-4 w-4" /> Actualiser</button>
          <button type="button" className={adminHeroActionClassName(false)} onClick={() => {
            setType("MONTHLY");
            setPeriodStart(dayjs().startOf("month").format("YYYY-MM-DD"));
            setPeriodEnd(dayjs().endOf("month").format("YYYY-MM-DD"));
            setFiscalYear(String(dayjs().year()));
            setCreateOpen(true);
          }}><Plus className="h-4 w-4" /> Nouvelle clôture mensuelle</button>
          <button type="button" className={adminHeroActionClassName(false)} onClick={() => {
            const year = dayjs().year();
            setType("YEARLY");
            setPeriodStart(`${year}-01-01`);
            setPeriodEnd(`${year}-12-31`);
            setFiscalYear(String(year));
            setCreateOpen(true);
          }}><Plus className="h-4 w-4" /> Nouvelle clôture annuelle</button>
        </div>}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <AdminKpiCard label="Dernière clôture mensuelle validée" value={lastMonthly ? periodLabel(lastMonthly) : "Aucune clôture validée"} />
        <AdminKpiCard label="Total facturé dernière clôture" value={lastMonthly ? money(lastMonthly.totalInvoiced) : "Non calculé"} />
        <AdminKpiCard label="Total encaissé dernière clôture" value={lastMonthly ? money(lastMonthly.totalPaid) : "Non calculé"} />
        <AdminKpiCard label="Impayés dernière clôture" value={lastMonthly ? money(lastMonthly.totalUnpaid) : "Non calculé"} />
        <AdminKpiCard label="Mois ouverts exercice actif" value={hints ? String(hints.openMonthsInActiveFiscalYear) : "Aucun exercice clôturé"} />
      </div>

      <section className="rounded-2xl border border-slate-800 bg-slate-950/70 shadow-xl">
        <div className="border-b border-slate-800 p-5">
          <h2 className="text-lg font-semibold text-slate-100">Historique des clôtures</h2>
          <p className="text-sm text-slate-400">Workflow strict : DRAFT → PREVIEWED → VALIDATED → LOCKED.</p>
        </div>
        {loading ? <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-8 w-8 animate-spin" /></div> : rows.length === 0 ? (
          <div className="p-8 text-center text-slate-400"><p className="font-semibold text-slate-200">Aucune clôture enregistrée</p><p className="mt-1 text-sm">Créez une clôture brouillon, prévisualisez, validez puis verrouillez la période.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm text-slate-200">
              <thead className="border-b border-slate-800 bg-slate-950/80 text-xs font-semibold uppercase text-slate-500">
                <tr>{["Période", "Type", "Statut", "Factures", "Total facturé", "Total encaissé", "Impayés", "Anomalies", "Créé par", "Validé par", "Verrouillé par", "Date création", "Actions"].map((h) => <th key={h} className="px-3 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>{rows.map((row) => (
                <tr key={row.id} className="border-b border-slate-800/80 hover:bg-slate-900/40">
                  <td className="px-3 py-3 font-medium text-white">{periodLabel(row)}</td>
                  <td className="px-3 py-3">{typeLabel(row.type)}</td>
                  <td className="px-3 py-3"><StatusBadge status={row.status} /></td>
                  <td className="px-3 py-3 text-right">{row.invoiceCount}</td>
                  <td className="px-3 py-3 text-right">{money(row.totalInvoiced)}</td>
                  <td className="px-3 py-3 text-right">{money(row.totalPaid)}</td>
                  <td className="px-3 py-3 text-right">{money(row.totalUnpaid)}</td>
                  <td className="px-3 py-3 text-right">{row.anomalyCount}</td>
                  <td className="px-3 py-3">{userName(row.createdBy)}</td>
                  <td className="px-3 py-3">{userName(row.validatedBy)}</td>
                  <td className="px-3 py-3">{userName(row.lockedBy)}</td>
                  <td className="px-3 py-3 text-slate-400">{dayjs(row.createdAt).format("DD/MM/YYYY HH:mm")}</td>
                  <td className="px-3 py-3"><div className="flex flex-wrap justify-end gap-1">
                    <Action onClick={() => { setSelected(row); setDetailOpen(true); }}><Eye className="h-3 w-3" /> Détail</Action>
                    {row.status === "DRAFT" ? <Action tone="emerald" onClick={() => void runPreview(row)}>Prévisualiser</Action> : null}
                    {row.status === "PREVIEWED" ? <Action tone="sky" onClick={() => void runAction(row, "validate")}><ShieldCheck className="h-3 w-3" /> Valider</Action> : null}
                    {row.status === "VALIDATED" && isAdmin ? <Action tone="amber" onClick={() => void runAction(row, "lock")}><Lock className="h-3 w-3" /> Verrouiller</Action> : null}
                    {row.status === "VALIDATED" || row.status === "LOCKED" ? <><Action onClick={() => void exportFile(row, "pdf")}><Download className="h-3 w-3" /> PDF</Action><Action onClick={() => void exportFile(row, "excel")}><FileSpreadsheet className="h-3 w-3" /> Excel</Action></> : null}
                    {row.status === "DRAFT" || row.status === "PREVIEWED" ? <Action tone="rose" onClick={() => void runAction(row, "cancel")}>Annuler</Action> : null}
                  </div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>

      {createOpen ? (
        <ClosingModalShell
          titleId="cloture-create-title"
          title="Nouvelle clôture mensuelle"
          onClose={() => setCreateOpen(false)}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-600 px-4 py-2.5 text-sm text-slate-100 hover:bg-slate-800 sm:py-2"
                onClick={() => setCreateOpen(false)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="inline-flex min-h-11 items-center justify-center rounded-xl border border-sky-600 px-4 py-2.5 text-sm font-semibold text-sky-100 hover:bg-sky-950/40 sm:py-2"
                onClick={() => void runPreview()}
              >
                Prévisualiser
              </button>
              <button
                type="button"
                className="inline-flex min-h-11 items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 sm:py-2"
                onClick={() => void createDraft()}
              >
                Créer clôture brouillon
              </button>
            </div>
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Date début">
              <input className="input" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
            </Field>
            <Field label="Date fin">
              <input className="input" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
            </Field>
            <Field label="Type">
              <select className="input" value={type} onChange={(e) => setType(e.target.value as FinancialClosingType)}>
                <option value="MONTHLY">Mensuelle</option>
                <option value="YEARLY">Annuelle</option>
              </select>
            </Field>
            <Field label="Exercice comptable">
              <input className="input" value={fiscalYear} onChange={(e) => setFiscalYear(e.target.value)} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Notes internes">
                <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
            </div>
          </div>
        </ClosingModalShell>
      ) : null}

      {previewOpen ? (
        <ClosingModalShell
          titleId="cloture-preview-title"
          title={selected ? `Rapport de prévisualisation ${periodLabel(selected)}` : "Rapport de prévisualisation"}
          onClose={() => setPreviewOpen(false)}
        >
          <div className="min-w-0 overflow-x-auto">
            {previewLoading ? (
              <div className="py-12 text-center text-slate-400">
                <Loader2 className="mx-auto h-8 w-8 animate-spin" aria-hidden />
              </div>
            ) : preview ? (
              <SnapshotView snapshot={preview} />
            ) : null}
          </div>
        </ClosingModalShell>
      ) : null}

      {detailOpen && selected ? (
        <ClosingModalShell
          titleId="cloture-detail-title"
          title={`Détail clôture ${periodLabel(selected)}`}
          onClose={() => setDetailOpen(false)}
        >
          <div className="min-w-0 space-y-6 overflow-x-auto">
            {selected.snapshotJson ? (
              <SnapshotView snapshot={selected.snapshotJson} />
            ) : (
              <p className="break-words text-sm text-slate-400">
                Aucun snapshot disponible. Prévisualisez la clôture avant validation.
              </p>
            )}
            <div className="border-t border-slate-800 pt-4">
              <h3 className="font-semibold text-white">Audit</h3>
              <div className="mt-3 space-y-2">
                {(selected.audits ?? []).length ? (
                  selected.audits?.map((audit) => (
                    <div key={audit.id} className="rounded-lg border border-slate-800 bg-slate-950/50 p-3 text-sm">
                      <p className="font-medium break-words text-slate-100">{audit.action}</p>
                      <p className="text-xs text-slate-500">
                        {dayjs(audit.createdAt).format("DD/MM/YYYY HH:mm")} · {userName(audit.actor)}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-400">Aucun audit disponible.</p>
                )}
              </div>
            </div>
          </div>
        </ClosingModalShell>
      ) : null}
    </div>
  );
}

function StatusBadge({ status }: { status: FinancialClosingStatus }): React.JSX.Element {
  const styles: Record<FinancialClosingStatus, string> = {
    DRAFT: "bg-slate-700/70 text-slate-200 ring-slate-600",
    PREVIEWED: "bg-sky-500/15 text-sky-200 ring-sky-500/30",
    VALIDATED: "bg-emerald-500/15 text-emerald-200 ring-emerald-500/30",
    LOCKED: "bg-amber-500/15 text-amber-200 ring-amber-500/30",
    CANCELLED: "bg-rose-500/15 text-rose-200 ring-rose-500/30",
  };
  return <span className={`rounded-full px-2 py-1 text-xs font-semibold ring-1 ${styles[status]}`}>{statusLabel(status)}</span>;
}

function Action({ children, onClick, tone = "slate" }: { children: React.ReactNode; onClick: () => void; tone?: "slate" | "emerald" | "sky" | "amber" | "rose" }): React.JSX.Element {
  const tones = {
    slate: "border-slate-600 text-slate-200 hover:bg-slate-800",
    emerald: "border-emerald-600 text-emerald-200 hover:bg-emerald-950/40",
    sky: "border-sky-600 text-sky-200 hover:bg-sky-950/40",
    amber: "border-amber-600 text-amber-200 hover:bg-amber-950/40",
    rose: "border-rose-600 text-rose-200 hover:bg-rose-950/40",
  };
  return <button type="button" className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium ${tones[tone]}`} onClick={onClick}>{children}</button>;
}

type ClosingModalShellProps = {
  titleId: string;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
};

function ClosingModalShell({
  titleId,
  title,
  onClose,
  children,
  footer,
}: ClosingModalShellProps): React.JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[120] flex min-w-0 items-end justify-center overflow-x-hidden bg-black/75 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="flex max-h-[90dvh] w-full max-w-5xl min-w-0 flex-col overflow-hidden rounded-t-2xl border border-slate-700 bg-slate-950 shadow-2xl sm:rounded-2xl">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-700 px-4 py-4 sm:px-6">
          <h2 id={titleId} className="min-w-0 flex-1 break-words pr-2 text-lg font-bold text-white sm:text-xl">
            {title}
          </h2>
          <button
            type="button"
            className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-800"
            onClick={onClose}
            aria-label="Fermer"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">{children}</div>
        {footer != null ? (
          <div className="shrink-0 border-t border-slate-800 bg-slate-950/98 px-4 py-4 sm:px-6">{footer}</div>
        ) : null}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return <label className="block text-sm"><span className="mb-1 block text-slate-300">{label}</span>{children}</label>;
}

function SnapshotView({ snapshot }: { snapshot: FinancialClosingSnapshotDto }): React.JSX.Element {
  return (
    <div className="min-w-0 space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Mini label="Factures" value={String(snapshot.counts.invoiceCount)} />
        <Mini label="Total facturé" value={money(snapshot.totals.totalTtc)} />
        <Mini label="Total encaissé" value={money(snapshot.totals.totalPaid)} />
        <Mini label="Impayés" value={money(snapshot.totals.totalUnpaid)} />
        <Mini label="Anomalies" value={String(snapshot.counts.anomalyCount)} />
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Anomalies">
          {snapshot.anomalies.length ? (
            snapshot.anomalies.slice(0, 8).map((a) => (
              <p key={`${a.entity}-${a.entityId}-${a.message}`} className="break-words text-sm text-slate-300">
                {a.type} · {a.entity} #{a.entityId} : {a.message}
              </p>
            ))
          ) : (
            <p className="text-sm text-slate-400">Aucune anomalie détectée.</p>
          )}
        </Panel>
        <Panel title="Impayés">
          {snapshot.unpaidInvoices.length ? (
            snapshot.unpaidInvoices.slice(0, 8).map((i) => (
              <p key={i.id} className="break-words text-sm text-slate-300">
                {i.number} · {i.client} · {money(i.remainingBalance)}
              </p>
            ))
          ) : (
            <p className="text-sm text-slate-400">Aucun impayé dans le snapshot.</p>
          )}
        </Panel>
      </div>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }): React.JSX.Element {
  return <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-3"><p className="text-xs text-slate-500">{label}</p><p className="mt-1 font-semibold text-white">{value}</p></div>;
}

function Panel({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
      <h3 className="mb-3 font-semibold text-white">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
